import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import CategoryTag from "@/components/base/CategoryTag";
import NewsCard from "@/components/feature/NewsCard";
import WorkCard from "@/components/feature/WorkCard";
import ShareFavorite from "./components/ShareFavorite";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";

interface MetaRow {
  label: string;
  value: string;
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const news = newsList.find((n) => n.id === id);
  const work = worksList.find((w) => w.id === id);

  const isNews = Boolean(news);
  const title = news ? news.title : work ? work.title : "";
  const summary = news ? news.summary : work ? work.summary : "";

  usePageMeta({
    title: title ? `${title} - 森泽佳奈官网` : "内容详情 - 森泽佳奈官网",
    description: summary || "森泽佳奈官方网站内容详情页面。",
    keywords: "森泽佳奈,森泽佳奈作品,森泽佳奈资讯",
  });

  if (!news && !work) {
    return (
      <div className="px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </div>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          未找到该内容
        </h1>
        <p className="text-sm text-foreground-500 mt-2">
          您访问的内容可能已被移动或不存在，请返回首页浏览其他内容。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const cover = news ? news.cover : work!.cover;
  const paragraphs = news ? news.content : work!.description;
  const tagLabel = news ? news.category : work!.type;
  const tagTone = news
    ? news.category === "活动公告"
      ? "accent"
      : "primary"
    : work!.category === "music"
    ? "accent"
    : "primary";

  const metaRows: MetaRow[] = news
    ? [
        { label: "发布日期", value: news.date },
        { label: "内容类型", value: news.category },
        { label: "相关标签", value: news.tags.join(" · ") },
      ]
    : [
        { label: "作品类型", value: work!.type },
        { label: "发行年份", value: `${work!.year} 年` },
        { label: "担任角色", value: work!.role },
        { label: "主创 / 制作", value: work!.director },
        { label: "发行地区", value: work!.region },
      ];

  const breadcrumbParent = news
    ? { label: "资讯", path: "/news" }
    : { label: "作品", path: "/works" };

  const relatedNews = news ? newsList.filter((n) => n.id !== news.id).slice(0, 3) : [];
  const relatedWorks = work
    ? worksList.filter((w) => w.category === work.category && w.id !== work.id).slice(0, 3)
    : [];

  const coverRatio = news ? "aspect-[16/9]" : work!.category === "music" ? "aspect-square" : "aspect-[3/4]";

  return (
    <div className="bg-background-50">
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={[breadcrumbParent, { label: title }]} />
        </div>
      </section>

      <article className="px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <CategoryTag label={tagLabel} tone={tagTone} />
              <span className="text-xs text-foreground-500">
                {news ? news.date : `${work!.year} · ${work!.region}`}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mt-4">{summary}</p>

            <div className="flex items-center justify-between gap-4 mt-6 flex-wrap">
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  森泽佳奈
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(title.length * 137) % 900 + 320} 次浏览（演示）
                </span>
              </div>
              <ShareFavorite id={id!} title={title} />
            </div>

            <div className={`w-full ${coverRatio} max-h-[520px] rounded-lg overflow-hidden bg-background-200 mt-8`}>
              <img
                src={cover}
                alt={title}
                title={`${title} - 森泽佳奈官网`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8">
              {paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="text-sm md:text-base text-foreground-700 leading-loose mb-5 whitespace-pre-line"
                >
                  {p}
                </p>
              ))}
            </div>

            {work?.tracks && work.tracks.length > 0 && (
              <div className="mt-6 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-play-list-2-line text-accent-500"></i>
                  曲目列表
                </h2>
                <ul className="flex flex-col divide-y divide-background-200">
                  {work.tracks.map((track, i) => (
                    <li key={track.name} className="flex items-center gap-4 py-3">
                      <span className="w-7 text-sm text-foreground-400 tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 text-sm md:text-base text-foreground-800">{track.name}</span>
                      <span className="text-xs md:text-sm text-foreground-500 tabular-nums">{track.duration}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="p-5 rounded-lg bg-background-100 border border-background-200 lg:sticky lg:top-28">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4">
                {isNews ? "资讯信息" : "作品信息"}
              </h2>
              <ul className="flex flex-col gap-3">
                {metaRows.map((row) => (
                  <li key={row.label} className="flex flex-col gap-1 pb-3 border-b border-background-200 last:border-0 last:pb-0">
                    <span className="text-xs text-foreground-500">{row.label}</span>
                    <span className="text-sm text-foreground-900">{row.value}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={breadcrumbParent.path}
                className="mt-5 inline-flex items-center gap-2 w-full justify-center px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回{breadcrumbParent.label}
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            {isNews ? "相关资讯推荐" : "同系列作品推荐"}
          </h2>
          {isNews ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {relatedWorks.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </article>
    </div>
  );
}